import { getChartHourLabel } from "../../../main/utils/utils";
import type { BaseUsinaValue } from "../models/dados-indicadores.model";

export type AccumulatedEnergyChartData = {
  accumulatedIdealEnergy?: BaseUsinaValue[];
  accumulatedGeneratedEnergy?: BaseUsinaValue[];
  pAccumulated?: BaseUsinaValue[];
};

export type AccumulatedEnergyChartSeries = {
  xLabels: string[];
  idealValues: (number | null)[];
  generatedValues: (number | null)[];
  p50Values: (number | null)[];
  collectDay: string;
};

type BuildAccumulatedEnergyChartSeriesOptions = {
  now?: Date;
};

const SIX_AM_MINUTES = 6 * 60;
const SAO_PAULO_TIMEZONE = "America/Sao_Paulo";
const EXPLICIT_TIMEZONE_PATTERN =
  /(?:[Zz]|[+-]\d{2}:?\d{2}(?::\d{2})?)$/;

function hasExplicitTimezone(dateTime: string): boolean {
  return EXPLICIT_TIMEZONE_PATTERN.test(dateTime.trim());
}

function parseChartDateTime(dateTime: string): number {
  const trimmed = dateTime.trim();
  const parsedDate = new Date(trimmed);

  if (Number.isNaN(parsedDate.getTime())) {
    return Number.NaN;
  }

  return Math.floor(parsedDate.getTime() / 60_000) * 60_000;
}

function normalizeDateTimeKey(dateTime: string): number {
  return parseChartDateTime(dateTime);
}

function getAccumulatedEnergyHourLabel(dateTime: string): string {
  const trimmed = dateTime.trim();

  if (!hasExplicitTimezone(trimmed)) {
    const isoTimeMatch = trimmed.match(/T(\d{2}:\d{2})/);
    if (isoTimeMatch?.[1]) {
      return isoTimeMatch[1];
    }
  }

  return getChartHourLabel(trimmed);
}

function getHourLabelMinutes(label: string): number {
  const [hours, minutes] = label.split(":").map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

function getSaoPauloNow(now: Date): { dateStr: string; totalMinutes: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: SAO_PAULO_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now);

  const year = parts.find((part) => part.type === "year")?.value ?? "0000";
  const month = parts.find((part) => part.type === "month")?.value ?? "01";
  const day = parts.find((part) => part.type === "day")?.value ?? "01";
  const hour = Number(parts.find((part) => part.type === "hour")?.value ?? 0);
  const minute = Number(
    parts.find((part) => part.type === "minute")?.value ?? 0,
  );

  return {
    dateStr: `${year}-${month}-${day}`,
    totalMinutes: hour * 60 + minute,
  };
}

function getPointDateInSaoPaulo(dateTime: string): string | null {
  const trimmed = dateTime.trim();
  const dateMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);

  if (!dateMatch) {
    return null;
  }

  if (!hasExplicitTimezone(trimmed)) {
    return dateMatch[1];
  }

  const parsedDate = new Date(trimmed);
  if (Number.isNaN(parsedDate.getTime())) {
    return dateMatch[1];
  }

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: SAO_PAULO_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(parsedDate);

  const year = parts.find((part) => part.type === "year")?.value ?? "0000";
  const month = parts.find((part) => part.type === "month")?.value ?? "01";
  const day = parts.find((part) => part.type === "day")?.value ?? "01";

  return `${year}-${month}-${day}`;
}

function isFutureChartPoint(
  dateTime: string,
  hourLabel: string,
  nowInSaoPaulo: { dateStr: string; totalMinutes: number },
): boolean {
  const pointDate = getPointDateInSaoPaulo(dateTime);

  if (!pointDate || pointDate !== nowInSaoPaulo.dateStr) {
    return false;
  }

  return getHourLabelMinutes(hourLabel) > nowInSaoPaulo.totalMinutes;
}

function buildSeriesMap(series?: BaseUsinaValue[]): Map<number, number> {
  const map = new Map<number, number>();

  series?.forEach((item) => {
    if (item.dateTime == null || item.value == null) {
      return;
    }

    const key = normalizeDateTimeKey(item.dateTime);
    if (Number.isNaN(key)) {
      return;
    }

    map.set(key, Number(item.value.toFixed(2)));
  });

  return map;
}

function buildTimeline(
  ideal?: BaseUsinaValue[],
  generated?: BaseUsinaValue[],
  p50?: BaseUsinaValue[],
): Array<{ key: number; dateTime: string }> {
  const timelineByKey = new Map<number, string>();

  const registerPoint = (dateTime?: string) => {
    if (!dateTime) {
      return;
    }

    const key = normalizeDateTimeKey(dateTime);
    if (Number.isNaN(key)) {
      return;
    }

    if (!timelineByKey.has(key)) {
      timelineByKey.set(key, dateTime);
    }
  };

  generated?.forEach((item) => registerPoint(item.dateTime));
  ideal?.forEach((item) => registerPoint(item.dateTime));
  p50?.forEach((item) => registerPoint(item.dateTime));

  return [...timelineByKey.entries()]
    .sort(([leftKey], [rightKey]) => leftKey - rightKey)
    .map(([key, dateTime]) => ({ key, dateTime }));
}

/** Keep last known Ideal/P50 value across later axis ticks (≤ now) where payload gaps. */
function forwardFillSeries(values: (number | null)[]): (number | null)[] {
  let lastValue: number | null = null;

  return values.map((value) => {
    if (value != null) {
      lastValue = value;
      return value;
    }
    return lastValue;
  });
}

function getCollectDay(dateTime?: string): string {
  if (!dateTime) {
    return "";
  }

  const trimmed = dateTime.trim();
  const parsedDate = new Date(trimmed);

  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  if (!hasExplicitTimezone(trimmed)) {
    const dateMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      return `${Number(day)}/${Number(month)}/${year}`;
    }
  }

  return parsedDate.toLocaleDateString("pt-BR", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    timeZone: SAO_PAULO_TIMEZONE,
  });
}

export function buildAccumulatedEnergyChartSeries(
  data: AccumulatedEnergyChartData,
  options?: BuildAccumulatedEnergyChartSeriesOptions,
): AccumulatedEnergyChartSeries {
  const nowInSaoPaulo = getSaoPauloNow(options?.now ?? new Date());
  const timeline = buildTimeline(
    data.accumulatedIdealEnergy,
    data.accumulatedGeneratedEnergy,
    data.pAccumulated,
  );

  const idealMap = buildSeriesMap(data.accumulatedIdealEnergy);
  const generatedMap = buildSeriesMap(data.accumulatedGeneratedEnergy);
  const p50Map = buildSeriesMap(data.pAccumulated);

  // Never show hours ahead of "now" (Ideal/P50 forecasts included).
  const visibleTimeline = timeline.filter(({ dateTime }) => {
    const hourLabel = getAccumulatedEnergyHourLabel(dateTime);
    return !isFutureChartPoint(dateTime, hourLabel, nowInSaoPaulo);
  });

  const xLabelsRaw = visibleTimeline.map(({ dateTime }) =>
    getAccumulatedEnergyHourLabel(dateTime),
  );
  const idealValuesRaw = forwardFillSeries(
    visibleTimeline.map(({ key }) => idealMap.get(key) ?? null),
  );
  const generatedValuesRaw = visibleTimeline.map(
    ({ key }) => generatedMap.get(key) ?? null,
  );
  const p50ValuesRaw = forwardFillSeries(
    visibleTimeline.map(({ key }) => p50Map.get(key) ?? null),
  );

  const sixAmIndex = xLabelsRaw.findIndex(
    (label) => getHourLabelMinutes(label) >= SIX_AM_MINUTES,
  );
  const chartStartIndex = sixAmIndex >= 0 ? sixAmIndex : 0;

  const slicedTimeline = visibleTimeline.slice(chartStartIndex);

  return {
    xLabels: xLabelsRaw.slice(chartStartIndex),
    idealValues: idealValuesRaw.slice(chartStartIndex),
    generatedValues: generatedValuesRaw.slice(chartStartIndex),
    p50Values: p50ValuesRaw.slice(chartStartIndex),
    collectDay: getCollectDay(slicedTimeline[0]?.dateTime),
  };
}
